import { memo, type MouseEvent as ReactMouseEvent, type ReactElement, useRef, useState } from 'react';
import { MaterialIcon } from './MaterialIcon.js';

/**
 * Contacts screen (Google Contacts integration). Presentational — the app owns data fetching and the
 * sync/confirm/dismiss actions and passes them in, so the view stays testable without a store. Two
 * panels: the synced contact directory (searchable) and a review queue of "link this [[mention]] to a
 * contact?" suggestions produced by reconciliation.
 */
export interface ContactView {
  contactId: string;
  displayName?: string;
  emails: string[];
  org?: string;
  title?: string;
  photoUrl?: string;
}

export interface ContactSuggestionView {
  suggestionId: string;
  entityName: string;
  contactName: string;
  score: number;
}

export interface ContactsListProps {
  contacts: ContactView[];
  /** People recently interacted with (email/calendar), shown as a rail above the directory. */
  recent?: ContactView[];
  suggestions: ContactSuggestionView[];
  query: string;
  onQueryChange: (q: string) => void;
  onSync: () => void;
  /** Merge the selected contacts into one; resolves when the reload finishes. */
  onMerge?: (contactIds: string[]) => Promise<void> | void;
  onConfirm: (suggestionId: string) => void;
  onDismiss: (suggestionId: string) => void;
  loading?: boolean;
  syncing?: boolean;
  error?: string;
  /** Reason connecting/syncing is unavailable (e.g. "Sign in first" / no Google account linked). */
  disabledReason?: string;
  /** Open a contact's detail on a plain click (selection stays Ctrl/Cmd-click + long-press) — Enh-B. */
  onOpen?: (contactId: string) => void;
}

function contactName(c: ContactView): string {
  // Fall back to org (companies saved without a person name) before the raw resourceName.
  return c.displayName || c.emails[0] || c.org || c.contactId;
}

function initials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 1).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

// Deterministic per-contact accent for the initials fallback (stable across renders/reloads).
const AVATAR_COLORS = [
  '#2563eb', '#0891b2', '#7c3aed', '#db2777', '#ea580c',
  '#16a34a', '#4f46e5', '#b91c1c', '#0d9488', '#9333ea',
];
function colorFor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length]!;
}

function Avatar({ contact, label }: { contact: ContactView; label: string }): ReactElement {
  if (contact.photoUrl) {
    return <img className="lapo-contact-avatar" src={contact.photoUrl} alt="" referrerPolicy="no-referrer" />;
  }
  return (
    <span
      className="lapo-contact-avatar lapo-contact-avatar-initial"
      style={{ backgroundColor: colorFor(contact.contactId || label) }}
      aria-hidden
    >
      {initials(label)}
    </span>
  );
}

/**
 * Selection is gesture-driven (no explicit mode): Ctrl/Cmd-click on desktop, long-press on mobile.
 * Once anything is selected (`selectionActive`), the whole card toggles on a plain tap and the
 * checkmarks show — iOS-photos style. `onSelect` is undefined when merging isn't available.
 */
function ContactCard({
  c,
  selected,
  selectionActive,
  onSelect,
  onOpen,
}: {
  c: ContactView;
  selected: boolean;
  selectionActive: boolean;
  onSelect?: (id: string) => void;
  onOpen?: (id: string) => void;
}): ReactElement {
  const name = contactName(c);
  const sub = [c.title, c.org].filter((s): s is string => !!s && s !== name).join(' · ');
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressed = useRef(false);

  const clearTimer = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = null;
  };
  const onTouchStart = () => {
    if (!onSelect) return;
    longPressed.current = false;
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      onSelect(c.contactId);
    }, 450);
  };
  const onClick = (e: ReactMouseEvent) => {
    if (longPressed.current) {
      longPressed.current = false; // the long-press already toggled; swallow the trailing click
      return;
    }
    if (onSelect && (e.ctrlKey || e.metaKey || selectionActive)) {
      e.preventDefault();
      onSelect(c.contactId);
      return;
    }
    // Plain click (not a selection gesture) opens the contact's detail (Enh-B).
    onOpen?.(c.contactId);
  };

  const cls = `lapo-contact-card${onSelect || onOpen ? ' is-pressable' : ''}${selectionActive ? ' is-selectable' : ''}${
    selected ? ' is-selected' : ''
  }`;
  return (
    <li
      className={cls}
      data-testid="contact-row"
      onClick={onClick}
      onTouchStart={onTouchStart}
      onTouchEnd={clearTimer}
      onTouchMove={clearTimer}
    >
      {selectionActive && (
        <span className="lapo-contact-check" aria-hidden>
          <MaterialIcon name={selected ? 'check_circle' : 'radio_button_unchecked'} size={20} />
        </span>
      )}
      <Avatar contact={c} label={name} />
      <span className="lapo-contact-main">
        <span className="lapo-contact-name" title={name}>
          {name}
        </span>
        {sub && <span className="lapo-contact-org">{sub}</span>}
        {c.emails[0] && (
          <span className="lapo-contact-email" title={c.emails[0]}>
            {c.emails[0]}
          </span>
        )}
      </span>
    </li>
  );
}

// Memoized (audit F3). Hoisted so the export can wrap it above the definition.
export const ContactsList = memo(ContactsListBase);

function ContactsListBase({
  contacts,
  recent,
  suggestions,
  query,
  onQueryChange,
  onSync,
  onMerge,
  onConfirm,
  onDismiss,
  loading,
  syncing,
  error,
  disabledReason,
  onOpen,
}: ContactsListProps): ReactElement {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [merging, setMerging] = useState(false);
  const selectionActive = selected.size > 0;
  const select = onMerge
    ? (id: string) =>
        setSelected((prev) => {
          const next = new Set(prev);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          return next;
        })
    : undefined;
  const clearSelection = () => setSelected(new Set());
  const doMerge = async () => {
    if (!onMerge || selected.size < 2) return;
    setMerging(true);
    try {
      await onMerge([...selected]);
      clearSelection();
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="lapo-contacts" data-testid="contacts-view">
      <header className="lapo-contacts-head">
        <h1 className="lapo-contacts-title">Contacts</h1>
        <button
          type="button"
          className="lapo-set-btn is-primary"
          disabled={!!disabledReason || syncing}
          title={disabledReason}
          onClick={onSync}
        >
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </header>

      {onMerge && !selectionActive && (
        <p className="lapo-contacts-muted lapo-contacts-selecthint">
          Tip: Ctrl/⌘-click (or long-press on mobile) contacts to select and merge duplicates.
        </p>
      )}
      {selectionActive && (
        <div className="lapo-contacts-mergebar" data-testid="contacts-mergebar">
          <span>{selected.size} selected</span>
          <button
            type="button"
            className="lapo-set-btn is-primary"
            disabled={selected.size < 2 || merging}
            onClick={() => void doMerge()}
          >
            {merging ? 'Merging…' : selected.size > 1 ? `Merge ${selected.size}` : 'Merge'}
          </button>
          <button type="button" className="lapo-set-btn" onClick={clearSelection} disabled={merging}>
            Cancel
          </button>
        </div>
      )}

      {disabledReason && <p className="lapo-contacts-muted">{disabledReason}</p>}
      {error && <p className="lapo-contacts-error">{error}</p>}

      {suggestions.length > 0 && (
        <section className="lapo-contacts-suggestions" data-testid="contact-suggestions">
          <h2 className="lapo-contacts-subhead">Suggested links</h2>
          <p className="lapo-contacts-muted">
            People mentioned in your notes that may be one of your contacts. Confirm to attach their
            email and details.
          </p>
          <ul className="lapo-contacts-suglist">
            {suggestions.map((s) => (
              <li key={s.suggestionId} className="lapo-contacts-sug" data-testid="contact-suggestion">
                <span className="lapo-contacts-sug-text">
                  <strong>{s.entityName}</strong>
                  <MaterialIcon name="arrow_forward" size={14} />
                  <span>{s.contactName}</span>
                </span>
                <span className="lapo-contacts-sug-actions">
                  <button type="button" className="lapo-set-btn is-primary" onClick={() => onConfirm(s.suggestionId)}>
                    Confirm
                  </button>
                  <button type="button" className="lapo-set-btn" onClick={() => onDismiss(s.suggestionId)}>
                    Dismiss
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!query && recent && recent.length > 0 && (
        <section className="lapo-contacts-recent" data-testid="contacts-recent">
          <h2 className="lapo-contacts-subhead">Recent</h2>
          <p className="lapo-contacts-muted">People you’ve emailed or met with lately.</p>
          <ul className="lapo-contacts-grid">
            {recent.map((c) => (
              <ContactCard
                key={`recent-${c.contactId}`}
                c={c}
                selected={selected.has(c.contactId)}
                selectionActive={selectionActive}
                onSelect={select}
                onOpen={onOpen}
              />
            ))}
          </ul>
        </section>
      )}

      <section className="lapo-contacts-directory">
        {!query && recent && recent.length > 0 && <h2 className="lapo-contacts-subhead">All contacts</h2>}
        <input
          type="search"
          className="lapo-set-input"
          aria-label="Search contacts"
          placeholder="Search contacts…"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
        />
        {loading && <p className="lapo-contacts-muted">Loading…</p>}
        {!loading && contacts.length === 0 && (
          <p className="lapo-contacts-muted">
            No contacts yet. Connect a Google account and sync to import your contacts.
          </p>
        )}
        <ul className="lapo-contacts-grid">
          {contacts.map((c) => (
            <ContactCard
              key={c.contactId}
              c={c}
              selected={selected.has(c.contactId)}
              selectionActive={selectionActive}
              onSelect={select}
              onOpen={onOpen}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}
